import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { createDefaultSellerReportDateRange, useSellerReportFilters } from '../useSellerReportFilters'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import { ConferenceOrderItem, ConferenceOrderSource } from '../../SellerQueue/types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface TicketBandConfig {
  key: string
  label: string
  minCents: number
  maxCents: number | null
}

interface BandCoverageRow {
  bandKey: string
  bandLabel: string
  totalOrders: number
  ninegridOrders: number
  totvsOrders: number
  ninegridSharePercent: number
  totvsSharePercent: number
  underrepresentedChannel: ConferenceOrderSource | 'none'
  gapPercent: number
}

interface SellerCoverageRow {
  sellerKey: string
  sellerLabel: string
  totalOrders: number
  ninegridOrders: number
  totvsOrders: number
  ninegridSharePercent: number
  totvsSharePercent: number
  underrepresentedChannel: ConferenceOrderSource | 'none'
  gapPercent: number
}

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const ticketBands: TicketBandConfig[] = [
  { key: 'up-to-100', label: 'Ate R$ 100', minCents: 0, maxCents: 10000 },
  { key: '100-200', label: 'R$ 100 a R$ 200', minCents: 10001, maxCents: 20000 },
  { key: '200-500', label: 'R$ 200 a R$ 500', minCents: 20001, maxCents: 50000 },
  { key: '500-1000', label: 'R$ 500 a R$ 1.000', minCents: 50001, maxCents: 100000 },
  { key: 'above-1000', label: 'Acima de R$ 1.000', minCents: 100001, maxCents: null },
]

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const readString = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return ''
}

const getSellerKeyAndLabel = (order: ConferenceOrderItem): { sellerKey: string; sellerLabel: string } => {
  const content = isRecord(order.content) ? order.content : {}
  const rawTotvsOrder = isRecord(content.rawTotvsOrder) ? content.rawTotvsOrder : {}
  const rawNinegridOrder = isRecord(content.rawNinegridOrder) ? content.rawNinegridOrder : {}
  const commissioneds = Array.isArray(rawTotvsOrder.commissioneds) ? rawTotvsOrder.commissioneds : []
  const firstCommissioned = isRecord(commissioneds[0]) ? commissioneds[0] : {}

  const sellerNameCandidates = [
    content.sellerName,
    rawNinegridOrder.sellerName,
    rawTotvsOrder.sellerName,
    firstCommissioned.sellerName,
    rawTotvsOrder.representativeName,
  ]

  const sellerCodeCandidates = [
    content.sellerCode,
    rawNinegridOrder.sellerCode,
    rawTotvsOrder.sellerCode,
    firstCommissioned.sellerCode,
    rawTotvsOrder.representativeCode,
  ]

  const sellerCpfCandidates = [
    content.sellerCpf,
    rawNinegridOrder.sellerCpf,
    rawTotvsOrder.sellerCpfCnpj,
    firstCommissioned.sellerCpfCnpj,
  ]

  const sellerName = sellerNameCandidates.map(readString).find(Boolean) || ''
  const sellerCode = sellerCodeCandidates.map(readString).find(Boolean) || ''
  const sellerCpf = sellerCpfCandidates.map(readString).find(Boolean) || ''

  const sellerLabel = sellerName || sellerCode || sellerCpf || 'Sem vendedor'
  const sellerKey = sellerCode || sellerCpf || sellerName || 'sem-vendedor'

  return {
    sellerKey,
    sellerLabel,
  }
}

const getBandForTicket = (ticketCents: number): TicketBandConfig => {
  return (
    ticketBands.find(
      (band) => ticketCents >= band.minCents && (band.maxCents === null || ticketCents <= band.maxCents)
    ) || ticketBands[ticketBands.length - 1]
  )
}

const getCoverageGap = (
  ninegridSharePercent: number,
  totvsSharePercent: number,
  baselineNinegridSharePercent: number,
  baselineTotvsSharePercent: number
): { underrepresentedChannel: ConferenceOrderSource | 'none'; gapPercent: number } => {
  const ninegridGap = baselineNinegridSharePercent - ninegridSharePercent
  const totvsGap = baselineTotvsSharePercent - totvsSharePercent

  if (ninegridGap <= 0 && totvsGap <= 0) {
    return {
      underrepresentedChannel: 'none',
      gapPercent: 0,
    }
  }

  if (ninegridGap > totvsGap) {
    return {
      underrepresentedChannel: 'ninegrid',
      gapPercent: ninegridGap,
    }
  }

  return {
    underrepresentedChannel: 'totvs',
    gapPercent: totvsGap,
  }
}

const buildBandCoverageRows = (
  report: SellerTicketAverageReport | null,
  baselineNinegridSharePercent: number,
  baselineTotvsSharePercent: number
): BandCoverageRow[] => {
  const grouped = new Map(
    ticketBands.map((band) => [
      band.key,
      {
        bandKey: band.key,
        bandLabel: band.label,
        totalOrders: 0,
        ninegridOrders: 0,
        totvsOrders: 0,
      },
    ])
  )

  if (!report?.items.length) {
    return ticketBands.map((band) => ({
      bandKey: band.key,
      bandLabel: band.label,
      totalOrders: 0,
      ninegridOrders: 0,
      totvsOrders: 0,
      ninegridSharePercent: 0,
      totvsSharePercent: 0,
      underrepresentedChannel: 'none',
      gapPercent: 0,
    }))
  }

  for (const order of report.items) {
    const source = order.source
    if (source !== 'ninegrid' && source !== 'totvs') {
      continue
    }

    const orderTotal = typeof order.total === 'number' ? Math.round(order.total) : 0
    const band = getBandForTicket(orderTotal)
    const current = grouped.get(band.key)

    if (!current) {
      continue
    }

    current.totalOrders += 1

    if (source === 'ninegrid') {
      current.ninegridOrders += 1
    } else {
      current.totvsOrders += 1
    }
  }

  return ticketBands.map((band) => {
    const row = grouped.get(band.key)!
    const ninegridSharePercent =
      row.totalOrders > 0 ? (row.ninegridOrders / row.totalOrders) * 100 : 0
    const totvsSharePercent =
      row.totalOrders > 0 ? (row.totvsOrders / row.totalOrders) * 100 : 0
    const gap = getCoverageGap(
      ninegridSharePercent,
      totvsSharePercent,
      baselineNinegridSharePercent,
      baselineTotvsSharePercent
    )

    return {
      ...row,
      ninegridSharePercent,
      totvsSharePercent,
      underrepresentedChannel: row.totalOrders > 0 ? gap.underrepresentedChannel : 'none',
      gapPercent: row.totalOrders > 0 ? gap.gapPercent : 0,
    }
  })
}

const buildSellerCoverageRows = (
  report: SellerTicketAverageReport | null,
  baselineNinegridSharePercent: number,
  baselineTotvsSharePercent: number
): SellerCoverageRow[] => {
  if (!report?.items.length) {
    return []
  }

  const enrichedLabelBySellerKey = new Map(report.rows.map((row) => [row.sellerKey, row.sellerLabel]))
  const grouped = new Map<
    string,
    {
      sellerKey: string
      sellerLabel: string
      totalOrders: number
      ninegridOrders: number
      totvsOrders: number
    }
  >()

  for (const order of report.items) {
    const source = order.source
    if (source !== 'ninegrid' && source !== 'totvs') {
      continue
    }

    const { sellerKey, sellerLabel } = getSellerKeyAndLabel(order)
    const current = grouped.get(sellerKey) || {
      sellerKey,
      sellerLabel: enrichedLabelBySellerKey.get(sellerKey) || sellerLabel,
      totalOrders: 0,
      ninegridOrders: 0,
      totvsOrders: 0,
    }

    current.sellerLabel = enrichedLabelBySellerKey.get(sellerKey) || sellerLabel || current.sellerLabel
    current.totalOrders += 1

    if (source === 'ninegrid') {
      current.ninegridOrders += 1
    } else {
      current.totvsOrders += 1
    }

    grouped.set(sellerKey, current)
  }

  return Array.from(grouped.values())
    .map((row) => {
      const ninegridSharePercent =
        row.totalOrders > 0 ? (row.ninegridOrders / row.totalOrders) * 100 : 0
      const totvsSharePercent =
        row.totalOrders > 0 ? (row.totvsOrders / row.totalOrders) * 100 : 0
      const gap = getCoverageGap(
        ninegridSharePercent,
        totvsSharePercent,
        baselineNinegridSharePercent,
        baselineTotvsSharePercent
      )

      return {
        ...row,
        ninegridSharePercent,
        totvsSharePercent,
        underrepresentedChannel: gap.underrepresentedChannel,
        gapPercent: gap.gapPercent,
      }
    })
    .sort((left, right) => right.gapPercent - left.gapPercent || right.totalOrders - left.totalOrders)
}

const channelLabel = (channel: ConferenceOrderSource | 'none'): string => {
  if (channel === 'ninegrid') {
    return 'Ninegrid'
  }

  if (channel === 'totvs') {
    return 'TOTVS'
  }

  return 'Nenhum'
}

const channelBadgeClass = (channel: ConferenceOrderSource | 'none'): string => {
  if (channel === 'ninegrid') {
    return 'badge badge-light-primary'
  }

  if (channel === 'totvs') {
    return 'badge badge-light-warning'
  }

  return 'badge badge-light-success'
}

const exportToCSV = (filename: string, bandRows: BandCoverageRow[], sellerRows: SellerCoverageRow[]): void => {
  let csv = 'sep=;\n'
  csv += 'COBERTURA POR FAIXA\n'
  csv += 'Faixa;Pedidos;Ninegrid;TOTVS;Ninegrid (%);TOTVS (%);Canal sub-representado;Gap (%)\n'
  bandRows.forEach((row) => {
    csv += `"${row.bandLabel}";${row.totalOrders};${row.ninegridOrders};${row.totvsOrders};${row.ninegridSharePercent.toFixed(1)};${row.totvsSharePercent.toFixed(1)};"${channelLabel(row.underrepresentedChannel)}";${row.gapPercent.toFixed(1)}\n`
  })

  csv += '\nCOBERTURA POR VENDEDOR\n'
  csv += 'Vendedor;Pedidos;Ninegrid;TOTVS;Ninegrid (%);TOTVS (%);Canal sub-representado;Gap (%)\n'
  sellerRows.forEach((row) => {
    csv += `"${row.sellerLabel}";${row.totalOrders};${row.ninegridOrders};${row.totvsOrders};${row.ninegridSharePercent.toFixed(1)};${row.totvsSharePercent.toFixed(1)};"${channelLabel(row.underrepresentedChannel)}";${row.gapPercent.toFixed(1)}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, bandRows: BandCoverageRow[], sellerRows: SellerCoverageRow[]): void => {
  const workbook = XLSX.utils.book_new()
  const bandSheet = XLSX.utils.json_to_sheet(
    bandRows.map((row) => ({
      Faixa: row.bandLabel,
      Pedidos: row.totalOrders,
      Ninegrid: row.ninegridOrders,
      TOTVS: row.totvsOrders,
      'Ninegrid (%)': row.ninegridSharePercent.toFixed(1),
      'TOTVS (%)': row.totvsSharePercent.toFixed(1),
      'Canal sub-representado': channelLabel(row.underrepresentedChannel),
      'Gap (%)': row.gapPercent.toFixed(1),
    }))
  )
  const sellerSheet = XLSX.utils.json_to_sheet(
    sellerRows.map((row) => ({
      Vendedor: row.sellerLabel,
      Pedidos: row.totalOrders,
      Ninegrid: row.ninegridOrders,
      TOTVS: row.totvsOrders,
      'Ninegrid (%)': row.ninegridSharePercent.toFixed(1),
      'TOTVS (%)': row.totvsSharePercent.toFixed(1),
      'Canal sub-representado': channelLabel(row.underrepresentedChannel),
      'Gap (%)': row.gapPercent.toFixed(1),
    }))
  )

  XLSX.utils.book_append_sheet(workbook, bandSheet, 'Por Faixa')
  XLSX.utils.book_append_sheet(workbook, sellerSheet, 'Por Vendedor')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (
  filename: string,
  bandRows: BandCoverageRow[],
  sellerRows: SellerCoverageRow[],
  report: SellerTicketAverageReport | null
): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Cobertura por Canal', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Cobertura por Faixa', margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Faixa', 'Pedidos', 'Ninegrid', 'TOTVS', 'Ninegrid (%)', 'TOTVS (%)', 'Canal', 'Gap (%)']],
    body: bandRows.map((row) => [
      row.bandLabel,
      row.totalOrders,
      row.ninegridOrders,
      row.totvsOrders,
      row.ninegridSharePercent.toFixed(1),
      row.totvsSharePercent.toFixed(1),
      channelLabel(row.underrepresentedChannel),
      row.gapPercent.toFixed(1),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  autoTable(pdf, {
    startY: (pdf as any).lastAutoTable.finalY + 10,
    head: [['Vendedor', 'Pedidos', 'Ninegrid', 'TOTVS', 'Ninegrid (%)', 'TOTVS (%)', 'Canal', 'Gap (%)']],
    body: sellerRows.map((row) => [
      row.sellerLabel,
      row.totalOrders,
      row.ninegridOrders,
      row.totvsOrders,
      row.ninegridSharePercent.toFixed(1),
      row.totvsSharePercent.toFixed(1),
      channelLabel(row.underrepresentedChannel),
      row.gapPercent.toFixed(1),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SellerChannelCoverageReportPage: FC = () => {
  const { filters, setFilters, isReady } = useSellerReportFilters<SellerTicketAverageFilters>({
    ...createDefaultSellerReportDateRange(),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatorio',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    if (isReady) {
      void loadReport()
    }
  }, [isReady, loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const baseline = useMemo(() => {
    const ninegrid = report?.sourceBreakdown.find((item) => item.source === 'ninegrid')?.orderCount || 0
    const totvs = report?.sourceBreakdown.find((item) => item.source === 'totvs')?.orderCount || 0
    const total = ninegrid + totvs

    return {
      ninegridSharePercent: total > 0 ? (ninegrid / total) * 100 : 0,
      totvsSharePercent: total > 0 ? (totvs / total) * 100 : 0,
    }
  }, [report])

  const bandRows = useMemo(
    () => buildBandCoverageRows(report, baseline.ninegridSharePercent, baseline.totvsSharePercent),
    [report, baseline.ninegridSharePercent, baseline.totvsSharePercent]
  )

  const sellerRows = useMemo(
    () => buildSellerCoverageRows(report, baseline.ninegridSharePercent, baseline.totvsSharePercent),
    [report, baseline.ninegridSharePercent, baseline.totvsSharePercent]
  )

  const underrepresentedBandRows = bandRows.filter((row) => row.underrepresentedChannel !== 'none' && row.totalOrders > 0)
  const underrepresentedSellerRows = sellerRows.filter((row) => row.underrepresentedChannel !== 'none')

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Cobertura por Canal',
            path: '/seller/reports/channel-coverage',
            isActive: true,
          },
        ]}
      >
        Relatório de Cobertura por Canal
      </PageTitle>

      <ReportShell
        title='Cobertura por Canal e Sub-representação'
        description='Compara a participação média dos canais no período contra faixas de ticket e vendedores para identificar onde Ninegrid ou TOTVS estão abaixo do esperado.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Cobertura-Canal-${timestamp}.csv`, bandRows, sellerRows)
            }} disabled={loading || (bandRows.length === 0 && sellerRows.length === 0)} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Cobertura-Canal-${timestamp}.xlsx`, bandRows, sellerRows)
            }} disabled={loading || (bandRows.length === 0 && sellerRows.length === 0)} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Cobertura-Canal-${timestamp}.pdf`, bandRows, sellerRows, report)
            }} disabled={loading || (bandRows.length === 0 && sellerRows.length === 0)} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Baseline Ninegrid</div>
                <div className='fs-2hx fw-bold text-primary'>
                  {percentFormatter.format(baseline.ninegridSharePercent)}%
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Baseline TOTVS</div>
                <div className='fs-2hx fw-bold text-warning'>
                  {percentFormatter.format(baseline.totvsSharePercent)}%
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Sinais de sub-representação</div>
                <div className='fs-2hx fw-bold text-danger'>
                  {(underrepresentedBandRows.length + underrepresentedSellerRows.length).toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm mb-4'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Cobertura por faixa de ticket</span>
              <span className='text-gray-600 mt-1'>Mostra onde cada canal fica abaixo da média geral dentro das faixas de valor.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : bandRows.some((row) => row.totalOrders > 0) ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Faixa</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Ninegrid</th>
                      <th className='text-end'>TOTVS</th>
                      <th className='text-end'>Canal sub-representado</th>
                      <th className='text-end'>Gap vs baseline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bandRows.map((row) => (
                      <tr key={row.bandKey}>
                        <td>
                          <div className='fw-bold text-dark'>{row.bandLabel}</div>
                        </td>
                        <td className='text-end fw-bold'>{row.totalOrders}</td>
                        <td className='text-end text-primary'>
                          {percentFormatter.format(row.ninegridSharePercent)}%
                        </td>
                        <td className='text-end text-warning'>
                          {percentFormatter.format(row.totvsSharePercent)}%
                        </td>
                        <td className='text-end'>
                          <span className={channelBadgeClass(row.underrepresentedChannel)}>
                            {channelLabel(row.underrepresentedChannel)}
                          </span>
                        </td>
                        <td className='text-end fw-bold text-danger'>
                          {row.underrepresentedChannel === 'none'
                            ? '-'
                            : `${percentFormatter.format(row.gapPercent)} pp`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum pedido encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Cobertura por vendedor</span>
              <span className='text-gray-600 mt-1'>Mostra vendedores onde um canal esta abaixo do esperado para o mix geral.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : sellerRows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Vendedor</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Ninegrid</th>
                      <th className='text-end'>TOTVS</th>
                      <th className='text-end'>Canal sub-representado</th>
                      <th className='text-end'>Gap vs baseline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellerRows.map((row) => (
                      <tr key={row.sellerKey}>
                        <td>
                          <div className='fw-bold text-dark'>
                            <SellerIdentity label={row.sellerLabel} />
                          </div>
                        </td>
                        <td className='text-end fw-bold'>{row.totalOrders}</td>
                        <td className='text-end text-primary'>
                          {percentFormatter.format(row.ninegridSharePercent)}%
                        </td>
                        <td className='text-end text-warning'>
                          {percentFormatter.format(row.totvsSharePercent)}%
                        </td>
                        <td className='text-end'>
                          <span className={channelBadgeClass(row.underrepresentedChannel)}>
                            {channelLabel(row.underrepresentedChannel)}
                          </span>
                        </td>
                        <td className='text-end fw-bold text-danger'>
                          {row.underrepresentedChannel === 'none'
                            ? '-'
                            : `${percentFormatter.format(row.gapPercent)} pp`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum vendedor encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { SellerChannelCoverageReportPage }
